import React, { useState } from "react";

export default function VideoPlayer({ videos, loading }) {
  const [active, setActive] = useState(null);

  if (loading) return (
    <div style={s.layout} className="video-layout">
      <div style={{ flex: 1 }}>
        <div style={sk("100%", 360)} />
        <div style={{ ...sk("100%", 60), marginTop: 12 }} />
      </div>
      <div style={s.sidebar} className="video-sidebar">
        {[1,2,3,4].map(i => <div key={i} style={{ ...sk("100%", 72), marginBottom: 8 }} />)}
      </div>
    </div>
  );

  if (!videos.length)
    return (
      <div style={s.empty}>
        <span style={{ fontSize: 40, display: "block", marginBottom: 10 }}>🎬</span>
        Select a subject to see video lectures
      </div>
    );

  const current = active || videos[0];

  return (
    <div>
      <div style={s.sectionTitle}>🎥 Video Lectures</div>

      {/* Two-column layout: player left, list right */}
      <div style={s.layout} className="video-layout">

        {/* LEFT — sticky player */}
        <div style={s.playerCol} className="video-player-col">
          <div style={s.playerCard}>
            <div style={s.playerRatio}>
              <iframe
                key={current.youtubeId}
                style={s.iframe}
                src={`https://www.youtube.com/embed/${current.youtubeId}?rel=0`}
                title={current.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          <div style={s.playerInfo}>
            <span style={s.chBadge}>{current.chapter}</span>
            <div style={s.playerTitle}>{current.title}</div>
            <div style={s.playerDur}>⏱ {current.duration}</div>
            <div style={s.playerCount}>{videos.indexOf(current) + 1} of {videos.length} videos</div>
          </div>
        </div>

        {/* RIGHT — scrollable video list */}
        <div style={s.sidebar} className="video-sidebar">
          <div style={s.sidebarHead}>All Videos</div>
          <div style={s.videoList}>
            {videos.map((v, i) => (
              <button
                key={v.id}
                style={{ ...s.videoItem, ...(current.id === v.id ? s.videoItemActive : {}) }}
                onClick={() => setActive(v)}
              >
                <div style={s.thumbWrap}>
                  <img
                    src={`https://img.youtube.com/vi/${v.youtubeId}/mqdefault.jpg`}
                    alt={v.title}
                    style={s.thumb}
                  />
                  <div style={{
                    ...s.playOverlay,
                    background: current.id === v.id ? "rgba(59,130,246,0.55)" : "rgba(0,0,0,0.4)"
                  }}>
                    {current.id === v.id
                      ? <span style={s.playingDot}>▶</span>
                      : <span style={{ fontSize: 14, color: "#fff" }}>▶</span>
                    }
                  </div>
                </div>
                <div style={s.vmeta}>
                  <div style={s.vTitle}>{v.title}</div>
                  <div style={s.vSub}>
                    <span style={s.chTag}>{v.chapter}</span>
                    <span>⏱ {v.duration}</span>
                  </div>
                </div>
                <span style={{ ...s.vNum, color: current.id === v.id ? "#3b82f6" : "#334155" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

const sk = (w, h) => ({ width: w, height: h, background: "#1e293b", borderRadius: 12 });

const s = {
  sectionTitle: {
    fontSize: 15, fontWeight: 600, color: "#e2e8f0",
    fontFamily: "'Sora',sans-serif", marginBottom: 16,
  },

  /* ── Two-column wrapper ── */
  layout: {
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    gap: 20,
    alignItems: "start",
  },

  /* ── Left: player ── */
  playerCol: {
    position: "sticky",
    top: 76,           /* below the 60px navbar + a little breathing room */
  },
  playerCard: {
    background: "#000",
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid #334155",
  },
  playerRatio: { position: "relative", paddingTop: "56.25%" },
  iframe: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%" },
  playerInfo: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 12,
    padding: "14px 16px",
    marginTop: 12,
  },
  chBadge: {
    display: "inline-block", fontSize: 10, fontWeight: 700,
    background: "#1d4ed8", color: "#bfdbfe",
    borderRadius: 6, padding: "3px 9px", marginBottom: 8,
    fontFamily: "'Sora',sans-serif",
  },
  playerTitle: {
    fontSize: 15, fontWeight: 600, color: "#f1f5f9",
    fontFamily: "'Sora',sans-serif", marginBottom: 5, lineHeight: 1.4,
  },
  playerDur: { fontSize: 12, color: "#64748b", fontFamily: "'Sora',sans-serif" },
  playerCount: {
    fontSize: 11, color: "#334155", fontFamily: "'Sora',sans-serif",
    marginTop: 6,
  },

  /* ── Right: scrollable list ── */
  sidebar: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 14,
    overflow: "hidden",
    maxHeight: "calc(100vh - 100px)",
    display: "flex",
    flexDirection: "column",
  },
  sidebarHead: {
    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.08em", color: "#475569",
    fontFamily: "'Sora',sans-serif",
    padding: "14px 16px 10px",
    borderBottom: "1px solid #0f172a",
    flexShrink: 0,
  },
  videoList: {
    overflowY: "auto",
    flex: 1,
    padding: "10px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  videoItem: {
    display: "flex", alignItems: "center", gap: 10,
    background: "#0f172a",
    border: "1px solid #1e293b",
    borderRadius: 10, padding: "8px 10px",
    cursor: "pointer", textAlign: "left", width: "100%",
    transition: "border-color 0.15s",
  },
  videoItemActive: {
    border: "1.5px solid #3b82f6",
    background: "#0d1f3c",
  },

  thumbWrap: {
    width: 80, height: 50,
    borderRadius: 7, overflow: "hidden",
    position: "relative", flexShrink: 0, background: "#000",
  },
  thumb: { width: "100%", height: "100%", objectFit: "cover" },
  playOverlay: {
    position: "absolute", inset: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  playingDot: { fontSize: 16, color: "#fff" },

  vmeta: { flex: 1, minWidth: 0 },
  vTitle: {
    fontSize: 12, fontWeight: 600, color: "#e2e8f0",
    fontFamily: "'Sora',sans-serif",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  vSub: {
    fontSize: 10, color: "#64748b", marginTop: 4,
    fontFamily: "'Sora',sans-serif",
    display: "flex", alignItems: "center", gap: 5,
  },
  chTag: {
    fontSize: 9, background: "#1e293b", color: "#475569",
    borderRadius: 4, padding: "2px 5px",
  },
  vNum: {
    fontSize: 11, fontWeight: 700,
    fontFamily: "monospace", flexShrink: 0,
  },

  empty: {
    textAlign: "center", color: "#475569",
    fontSize: 15, padding: "60px 0",
    fontFamily: "'Sora',sans-serif",
  },
};
